import { animalSensitivitySelectOptions, binarySelectOptions, useRouter } from '@application/utils';
import type { PromoNewsletterFormValues, PromoNewsletterProps } from '@presentation/components';
import {
  Button,
  Container,
  InputCheckboxField,
  InputField,
  InputLabel,
  InputRadioField,
  InputSelectField,
  InputWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { ClassNames, useTheme } from '@emotion/react';
import { EmailContactCreatedFromEnum } from '@gql/graphql';
import { Formik } from 'formik';
import type { FC } from 'react';
import * as Styled from './styled';

export const LeadForm: FC<PromoNewsletterProps> = (props) => {
  const {
    form,
    hideHasADogField = false,
    hideLookingForField = false,
    hideSensitivityField = false,
    ...rest
  } = props;
  const { translate } = useTranslation();
  const { routeLocationDescriptor } = useRouter();
  const theme = useTheme();
  const yesNoOptions = binarySelectOptions(translate);
  const noDogChoiceOptions = [
    {
      value: 'puppy',
      label: translate('common.buttons.yes'),
    },
    {
      value: 'information',
      label: translate('common.buttons.no'),
    },
  ];
  const sensitivitiesEnum = animalSensitivitySelectOptions(translate);
  const shouldDisplaySensitivityField = (
    hasADogIsTouched: boolean | undefined,
    hasADogValue: boolean | undefined,
  ) => {
    if (hideSensitivityField) return false;
    return hasADogIsTouched === true && hasADogValue === true;
  };

  const shouldDisplayLookingForField = (
    hasADogIsTouched: boolean | undefined,
    hasADogValue: boolean | undefined,
    createdFrom: EmailContactCreatedFromEnum | undefined,
  ) => {
    if (hideLookingForField) return false;
    return (
      hasADogIsTouched === true &&
      hasADogValue === false &&
      createdFrom !== EmailContactCreatedFromEnum.BREED_INTEREST
    );
  };
  return (
    <Container>
      <Styled.Card {...rest}>
        <Styled.Title>{translate('quiz.noUser.title')}</Styled.Title>
        <Formik<PromoNewsletterFormValues> {...form}>
          {({ submitForm, errors, isSubmitting, values, touched }) => (
            <>
              <InputWrapper>
                <InputField
                  placeholder={translate('common.inputs.email.placeholder')}
                  name="email"
                  type="email"
                />
              </InputWrapper>
              {hideHasADogField ? null : (
                <InputWrapper>
                  <InputLabel required>{translate('common.inputs.hasADog')}</InputLabel>
                  <InputRadioField<boolean>
                    name="hasADog"
                    variant="blue"
                    grow
                    options={yesNoOptions}
                    hasError={!!errors.hasADog}
                  />
                </InputWrapper>
              )}

              {shouldDisplaySensitivityField(touched.hasADog, values.hasADog) ? (
                <InputWrapper>
                  <InputLabel required={values?.hasADog === true && touched?.hasADog === true}>
                    {translate('common.sections.leadForm.sensitivity.label')}
                  </InputLabel>

                  <InputSelectField
                    name="sensitivity"
                    variant="new"
                    options={sensitivitiesEnum}
                    hasDropdownIcon
                    hasError={!!errors?.sensitivity}
                  />
                </InputWrapper>
              ) : null}
              {shouldDisplayLookingForField(touched.hasADog, values.hasADog, values.createdFrom) ? (
                <InputWrapper>
                  <InputLabel required>
                    {translate('common.sections.leadForm.hasADog.label')}
                  </InputLabel>
                  <ClassNames>
                    {({ css: classNameCss }) => {
                      const wrapperClassname = classNameCss(Styled.inputRadioWrapperStyle(theme));
                      return (
                        <InputRadioField<string>
                          name="lookingFor"
                          variant="blue"
                          grow
                          separated
                          options={noDogChoiceOptions}
                          wrapperClass={wrapperClassname}
                        />
                      );
                    }}
                  </ClassNames>
                </InputWrapper>
              ) : null}
              <InputWrapper>
                <InputCheckboxField name="marketingEmailConsent">
                  <Styled.TermsContainer
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: translate('common.inputs.termsPolicyEmailMarketing.label').replace(
                        '$link',
                        routeLocationDescriptor('privacy').pathname ?? '',
                      ),
                    }}
                  />
                </InputCheckboxField>
              </InputWrapper>
              <InputWrapper>
                <Button
                  color="blue"
                  onClick={submitForm}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {translate('common.buttons.showResults')}
                </Button>
              </InputWrapper>
            </>
          )}
        </Formik>
      </Styled.Card>
    </Container>
  );
};
